import React from 'react';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { services, WHATSAPP_LINK } from '../data/mock';
import SEO from '../components/SEO';

const Services = () => {
  return (
    <div className="min-h-screen pt-16">
      <SEO 
        title="Serviços de Energia Solar - W3Solar | Residencial, Comercial e Industrial"
        description="Soluções completas em energia solar fotovoltaica. Atendemos residências, empresas e indústrias com instalação rápida e garantia estendida."
        keywords="serviços energia solar, instalação painéis solares, energia solar residencial, energia solar comercial, energia solar industrial"
      />
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Nossos Serviços
            </h1>
            <p className="text-xl text-gray-600">
              Soluções completas em energia solar fotovoltaica para todos os tipos de projetos
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div
                key={service.id}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-96 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <Card className="border-2 hover:border-emerald-400 transition-all">
                    <CardHeader>
                      <CardTitle className="text-3xl mb-2">{service.title}</CardTitle>
                      <CardDescription className="text-lg">{service.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <h4 className="font-semibold text-lg mb-4 text-gray-900">
                        O que está incluso:
                      </h4>
                      <ul className="space-y-3 mb-6">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="h-6 w-6 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                        <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white w-full">
                          Solicitar Orçamento
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Serviços Adicionais
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Oferecemos suporte completo para seu sistema de energia solar
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Manutenção Preventiva',
                description: 'Inspeções regulares para garantir máxima eficiência',
                features: ['Limpeza semestral', 'Check-up completo', 'Relatório de performance']
              },
              {
                title: 'Monitoramento 24/7',
                description: 'Acompanhe a geração em tempo real',
                features: ['App mobile', 'Alertas automáticos', 'Histórico detalhado']
              },
              {
                title: 'Expansão de Sistema',
                description: 'Aumente a capacidade quando precisar',
                features: ['Análise técnica', 'Projeto de expansão', 'Instalação integrada']
              },
              {
                title: 'Consultoria Energética',
                description: 'Otimize seu consumo de energia',
                features: ['Auditoria energética', 'Plano de eficiência', 'ROI detalhado']
              }
            ].map((item, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-xl">{item.title}</CardTitle>
                  <CardDescription>{item.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {item.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronto para começar seu projeto?
          </h2>
          <p className="text-xl mb-8 text-emerald-50">
            Entre em contato e receba um orçamento personalizado
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/orcamento">
              <Button size="lg" className="bg-white text-emerald-700 hover:bg-gray-100 text-lg px-8">
                Solicitar Orçamento Grátis
              </Button>
            </Link>
            <Link to="/calculadora">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-8">
                Calcular Economia
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
